import React from "react";
import { Box, Chip, Skeleton, Typography } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { DOMAIN_NAMES, STATUS } from "./trendDisplay";

const NAME_PATTERN = new RegExp(`(${DOMAIN_NAMES.join("|")})`, "g");

// Domain names in the text become buttons that highlight their graph.
function LinkedText({ text, statusByName, idByName, pinned, onPreview, onPin }) {
    return String(text)
        .split(NAME_PATTERN)
        .map((part, index) => {
            const id = idByName[part];
            if (!id) return part;
            const status = STATUS[statusByName[part]] || STATUS.none;
            return (
                <Box
                    key={index}
                    component="button"
                    type="button"
                    data-domain-ref={id}
                    aria-pressed={pinned === id}
                    onMouseEnter={() => onPreview(id)}
                    onMouseLeave={() => onPreview(null)}
                    onFocus={() => onPreview(id)}
                    onBlur={() => onPreview(null)}
                    onClick={() => onPin(id)}
                    sx={{
                        display: "inline",
                        m: 0,
                        p: "0 1px",
                        border: 0,
                        borderBottom: `2px solid ${status.color}`,
                        borderRadius: 0,
                        background: "none",
                        font: "inherit",
                        lineHeight: "inherit",
                        color: "inherit",
                        cursor: "pointer",
                        "&:hover, &[aria-pressed='true']": { borderBottomColor: status.ink, bgcolor: status.background },
                        "&:focus-visible": { outline: "3px solid #0080d3", outlineOffset: 2 },
                    }}
                >
                    {part}
                </Box>
            );
        });
}

// The AI Well-Being Overview, styled after AISummary without its disclaimer: a basis line, then only the
// sections that have domains (What's going well, Needs attention, Warnings), each with a headline and summary.
export default function TrendsOverview({ overview, refreshing, hasData, domains, pinned, onPreview, onPin }) {
    const showSkeleton = !overview && refreshing;
    const sections = overview?.sections || [];
    const statusByName = Object.fromEntries(domains.map((d) => [d.name, d.assessment?.status || "none"]));
    const idByName = Object.fromEntries(domains.map((d) => [d.name, d.id]));
    const linked = (text) => (
        <LinkedText
            text={text}
            statusByName={statusByName}
            idByName={idByName}
            pinned={pinned}
            onPreview={onPreview}
            onPin={onPin}
        />
    );

    return (
        <Box
            component="section"
            aria-labelledby="pearli-trends-overview-title"
            aria-busy={refreshing ? "true" : "false"}
            sx={{ borderRadius: 2, bgcolor: "background.paper", p: 2.5, boxShadow: 1, width: "100%" }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AutoAwesomeOutlinedIcon sx={{ fontSize: 20, color: "#0E8B63" }} />
                    <Typography id="pearli-trends-overview-title" variant="h6" component="h2" fontWeight={600} sx={{ color: "#154C3A" }}>
                        AI Well-Being Overview
                    </Typography>
                </Box>
                {refreshing && <Chip size="small" label="Updating..." color="primary" variant="outlined" />}
            </Box>

            {showSkeleton ? (
                <Box aria-hidden="true">
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" width="80%" />
                </Box>
            ) : overview?.failed && hasData ? (
                <Box sx={{ minHeight: 100, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: "text.secondary", fontSize: "0.9rem" }}>
                    The overview could not be loaded right now. Please try again later.
                </Box>
            ) : !hasData || !sections.length ? (
                <Box
                    sx={{
                        minHeight: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        color: "text.secondary",
                        fontSize: "0.9rem",
                        opacity: 0.8,
                    }}
                >
                    No assessment data available for the selected filters.
                </Box>
            ) : (
                <>
                    {overview.basis && (
                        <Typography sx={{ mt: -0.75, mb: 1.5, fontSize: 12, color: "#64748B" }}>{overview.basis}</Typography>
                    )}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {sections.map((section) => {
                            const status = STATUS[section.key];
                            return (
                                <Box
                                    key={section.key}
                                    component="section"
                                    data-status={section.key}
                                    data-domains={section.domains.join(" ")}
                                    aria-labelledby={`pearli-trends-${section.key}`}
                                    sx={{
                                        p: "12px 16px",
                                        borderRadius: 2,
                                        border: "1px solid #E2E8F0",
                                        borderLeft: `4px solid ${status.color}`,
                                        bgcolor: "#fff",
                                    }}
                                >
                                    <Typography
                                        id={`pearli-trends-${section.key}`}
                                        component="h3"
                                        sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25, fontSize: 13, fontWeight: 600, lineHeight: "20px", color: status.ink }}
                                    >
                                        <Box component="span" sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: status.color, flexShrink: 0 }} />
                                        {section.title}
                                    </Typography>
                                    <Typography sx={{ maxWidth: 1000, mb: 0.5, fontSize: 16, fontWeight: 600, lineHeight: "24px", color: "#141414" }}>
                                        {linked(section.headline)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            maxWidth: 1000,
                                            fontSize: 14,
                                            color: "rgba(0,0,0,0.6)",
                                            lineHeight: 1.6,
                                            "& button": { color: "#141414", fontWeight: 500 },
                                        }}
                                    >
                                        {linked(section.summary)}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </Box>
    );
}
